/**
 * WHERE THE COACHES AND THE OFFICIALS STAND.
 *
 * ── Read this before assuming the game has a referee ────────────────────────
 *
 * IT DOES NOT, AND THIS FILE DOES NOT ADD ONE.
 *
 *   - `WorldState` has no official. `PlayerArrays` is exactly `MAX_PLAYERS = 22`
 *     wide and every array in it is that long; there is no 23rd slot and adding
 *     one is a simulation change, not a rendering change.
 *   - The rules layer holds referee DECISIONS — `pendingWhistleTicks`,
 *     `RefereeCard`, `restartX/restartZ`, `REFEREE_REACTION_MIN_MS` — and never a
 *     referee POSITION. `RenderState.refSignal` carries the (x,z) of the SPOT
 *     being signalled, which is not where the man is standing.
 *   - A `render/referee.ts` used to exist and was deleted with the old character
 *     pipeline (`subsystems.ts` still marks the hole). Its own header said the
 *     same thing: it derived three officials from `RenderState` and added
 *     nothing to the sim.
 *
 * So everything below is PRESENTATION ONLY, derived per frame from the ball, the
 * 22 player transforms and the pitch config — exactly the inputs a subsystem is
 * allowed to read (§15.2, graphics is strictly downstream). An official drawn
 * here cannot influence a decision, cannot be fouled and does not exist to the
 * simulation. That is a deliberate limitation, not an oversight, and any real
 * refereeing behaviour is a sim change that has to be asked for.
 *
 * ── The coaches are different: the anchors already exist ────────────────────
 *
 * `render/sideline.ts` builds a populated technical area — a pacing hero MANAGER
 * in the home area, instanced standing coaches in both — and the anchor
 * arithmetic below is the SAME arithmetic, reproduced because those constants
 * are module-private there. Keep in sync with `sideline.ts`; if a dugout moves,
 * both files move.
 */

/** Mirrors `sideline.ts` / `stadium.ts`. If a dugout moves, these move. */
export const DUGOUT_GAP_X = 4;
export const DUGOUT_LEN = 8;
/** Home dugout centre at x = −8, away at +8. */
export const DUGOUT_CX = DUGOUT_GAP_X + DUGOUT_LEN * 0.5;
/** The technical-area line, metres outside the −z touchline. */
export const TECH_Z_OFF = 0.7;

export interface Placement {
  readonly x: number;
  readonly z: number;
  /** Radians, sim convention (yaw measured +X→+Z). */
  readonly yaw: number;
}

export interface PitchDims {
  readonly length: number;
  readonly width: number;
}

/**
 * The two technical areas.
 *
 * The home coach stands one metre inside his own dugout centre, the away coach
 * the mirror of it, and both face the pitch. `sideline.ts` puts its instanced
 * "away head coach" on exactly `(+DUGOUT_CX, techZ)` and its pacing hero manager
 * on `(−DUGOUT_CX, techZ)`, so a cast coach dropped on these anchors replaces a
 * procedural figure rather than standing next to one — the subsystem hides the
 * procedural one it is covering.
 */
/**
 * How far a coach paces either side of his dugout centre, metres.
 *
 * The technical area is `DUGOUT_LEN` (8 m) wide, so 2.6 keeps him inside it
 * with room at both ends and never walks him onto the touchline.
 */
const COACH_PACE_M = 2.6;
/** Seconds for one full there-and-back. A restless walk, not a march. */
const COACH_PACE_PERIOD_S = 11;
/**
 * The away coach is offset half a cycle so the two are never in step. Two men
 * pacing in perfect sync across the halfway line reads as one animation played
 * twice, which is exactly what it would be.
 */
const COACH_PHASE_OFFSET = Math.PI;

export function coachPlacement(
  side: 'home' | 'away',
  pitch: PitchDims,
  timeS = 0,
): Placement {
  // ⚠ THE TECHNICAL AREA IS AT THE DUGOUT, NOT ON THE TOUCHLINE (owner,
  // 2026-08-16: "coaches are on pitch"). TECH_Z_OFF (0.7 m outside the line)
  // is a real technical area's depth AND correct for `stadium.ts`'s bowl,
  // where the dugout is right there. The shipped FLAGSHIP bowl puts its
  // dugouts at z = -44, so a coach on the old line stood ten metres out in
  // front of his own shelter, alone at the edge of the grass - which from the
  // broadcast camera reads as a man standing on the pitch. He now paces in
  // front of the dugout he actually belongs to.
  const techZ = FLAGSHIP_DUGOUT_Z !== null
    ? FLAGSHIP_DUGOUT_Z + 2.0
    : -(pitch.width * 0.5 + TECH_Z_OFF);
  const cx = side === 'home' ? -DUGOUT_CX : DUGOUT_CX;

  // ⚠ HE PACES. Owner: "add the walk animations for coach". No new clip was
  // needed for that — staff already run on the gait ladder, and the ladder
  // picks Walk from the speed their placement actually moves at. A coach whose
  // anchor never moved was therefore pinned to Idle by arithmetic, not by any
  // missing animation. Giving the anchor a slow oscillation is the whole fix.
  //
  // sin, not a triangle wave: a linear there-and-back reverses at full speed at
  // each end, and the gait ladder would read that as a walk that never
  // decelerates. sin eases to zero at the turns, so he slows, turns, and sets
  // off again — and `placeStaff` already faces him along his travel direction,
  // so he turns to walk back rather than moon-walking.
  const phase = (timeS / COACH_PACE_PERIOD_S) * Math.PI * 2
    + (side === 'away' ? COACH_PHASE_OFFSET : 0);
  const x = cx + Math.sin(phase) * COACH_PACE_M;

  // Face +z, i.e. into the pitch. Sim yaw: +X→+Z, so +π/2 looks down +z. This
  // is the STANDING facing; once he is moving, `placeStaff` overrides it with
  // his direction of travel.
  return { x, z: techZ, yaw: Math.PI / 2 };
}

/**
 * The referee, on the Law-5 diagonal.
 *
 * The diagonal runs corner to corner; the referee holds a position offset from
 * the ball ALONG it, which keeps play between him and the near assistant and is
 * what makes a referee read as a referee rather than as a 23rd player chasing
 * the ball. He is pulled towards the ball but never onto it: `TRAIL_M` is the
 * gap that stops him standing in the passing lane the AI is using.
 *
 * Deterministic and allocation-free: the caller owns the smoothing.
 */
const REF_TRAIL_M = 9;
const REF_DIAGONAL = 0.42;
/**
 * How sharply the referee commits to a half as the ball crosses the middle.
 *
 * Feeds `tanh(t * gain)`, replacing the two sign flips that used to sit at
 * t = 0. At 2.5 he is at 99 % of full diagonal offset by a quarter of the way
 * up the pitch, so deep in either half he stands where he always stood; raise
 * it and the crossover sharpens back toward the teleport, lower it and he
 * drifts around the centre circle instead of picking a side.
 */
const REF_COMMIT_GAIN = 2.5;
/**
 * How far to the side of play the referee always stands (m). At kickoff the
 * diagonal offset is zero by construction, so without this he walked onto the
 * centre spot; with it he is off the ball everywhere, which is what the
 * diagonal system looks like.
 */
const REF_STANDOFF_M = 1.2;
/**
 * Extra clearance while play is on the halfway line, where the diagonal offset
 * is zero by construction and he would otherwise stand on the centre spot
 * among the kickoff takers. Fades out as play commits to a half.
 */
const REF_KICKOFF_CLEAR_M = 5.5;

export function refereePlacement(
  ballX: number,
  ballZ: number,
  pitch: PitchDims,
): Placement {
  const halfL = pitch.length * 0.5;
  const halfW = pitch.width * 0.5;
  // Position along the diagonal, parameterised by where the ball is lengthwise.
  const t = Math.max(-1, Math.min(1, ballX / halfL));

  // ⚠ TWO STEP FUNCTIONS USED TO LIVE ON THIS LINE AND THEY ARE WHY HE
  // TELEPORTED. `Math.sign(ballX || 1)` and `(t >= 0 ? 1 : -1)` both flip the
  // instant the ball crosses the halfway line, and neither is small: the trail
  // term jumps 2 x 9 x 0.35 = 6.3 m and the diagonal term jumps
  // 2 x 0.42 x halfW x 0.5 — about 14 m on a full-size pitch. A midfield pass
  // over the centre circle therefore moved the referee twenty metres between
  // one frame and the next, which is exactly the "teleporting" the owner filmed.
  //
  // `tanh` is the same shape either side and continuous through zero: he still
  // commits to the correct half, still swaps diagonal as play turns, and now
  // crosses over instead of cutting. The gain sets how sharply he commits —
  // 2.5 reaches 0.99 of full offset by a quarter of the pitch, so his position
  // in either half is unchanged to within centimetres of the old behaviour.
  // Only the seam is different, which is the only place it was ever wrong.
  const commit = Math.tanh(t * REF_COMMIT_GAIN);
  const x = Math.max(-halfL + 2, Math.min(halfL - 2, ballX - REF_TRAIL_M * commit * 0.35));
  // ⚠ NEVER ON THE BALL (owner, 2026-08-16: "refree during game start standing
  // in middle of the ground"). The diagonal term is proportional to `commit`,
  // which is tanh(0) = 0 when the ball sits on the halfway line - so at KICKOFF
  // the referee's target was the centre spot itself, and he stood in the middle
  // of the circle among the takers. A real referee always works from one side;
  // REF_STANDOFF_M is that side, and the diagonal swings around it.
  // ⚠ THE STANDOFF MUST NOT CANCEL THE DIAGONAL. A flat +7 pushed him off the
  // centre spot but also turned the LEFT-half diagonal (-7.1) into +0.35, so
  // he stood on the same side for both halves - refereeMotion's acceptance
  // caught it. The standoff is therefore two parts: a small constant that
  // never outweighs the diagonal, plus a kickoff-only term that fades out by
  // a third of the way up the pitch, exactly where the diagonal takes over.
  // Continuous, so the halfway-line teleport stays fixed.
  const diag = REF_DIAGONAL * halfW * commit * 0.5;
  const nearCentre = 1 - Math.min(1, Math.abs(commit) * 3);
  const lateral = diag + REF_STANDOFF_M + REF_KICKOFF_CLEAR_M * nearCentre;
  const z = Math.max(-halfW + 2, Math.min(halfW - 2, ballZ + lateral));
  const yaw = Math.atan2(ballZ - z, ballX - x);
  return { x, z, yaw };
}

/**
 * The assistant referee, on the touchline, level with the offside line.
 *
 * The offside line is the SECOND-LAST defender of the side being attacked, and
 * that is derivable from the transforms the renderer already has — it is not the
 * sim's own offside state and must not be confused with it. When there are not
 * two defenders to measure (a scenario, a training drill), he holds the halfway
 * line, which is where a linesman stands when nothing is happening.
 */
export function assistantPlacement(
  playerX: Float32Array,
  active: Uint8Array,
  team: Uint8Array,
  defendingTeam: 0 | 1,
  attackDirIsPositiveX: boolean,
  pitch: PitchDims,
): Placement {
  const halfL = pitch.length * 0.5;
  const halfW = pitch.width * 0.5;
  const xs: number[] = [];
  for (let i = 0; i < active.length; i++) {
    if (active[i] !== 1 || team[i] !== defendingTeam) continue;
    xs.push(playerX[i] as number);
  }
  let line = 0;
  if (xs.length >= 2) {
    xs.sort((a, b) => (attackDirIsPositiveX ? b - a : a - b));
    line = xs[1]!;
  }
  const x = Math.max(-halfL + 1, Math.min(halfL - 1, line));
  // The assistant works the touchline on the FAR side from the dugouts, so he
  // is never inside the technical area the coaches occupy.
  // 1.1 m read as ON the line from the broadcast camera (owner, 2026-08-16:
  // "line refree was also inside"). A real assistant does hug the touchline,
  // but at this camera distance the extra metre is the difference between a
  // linesman and a 23rd player.
  const z = halfW + 2.2;
  return { x, z, yaw: -Math.PI / 2 };
}

/**
 * A substitute on the bench, by index along his own dugout.
 *
 * ⚠ THEY STAND. There is no seated clip in the pack — every animation on the
 * 74-bone rig is a footballer's, authored upright — so a `sit` here would be a
 * name for a pose nobody has made. Standing subs in the technical area is what
 * the assets can honestly draw today, and it is also what warming-up subs
 * actually do; a seated row is the better shot and it needs a clip authored
 * before it can exist.
 *
 * They line the dugout BEHIND the coach's pacing lane (`BENCH_Z_OFF` is deeper
 * than `TECH_Z_OFF`), so a coach walking his 2.6 m never walks through them.
 */
export const BENCH_Z_OFF = 2.6;

/**
 * The FLAGSHIP bowl's dugout line in game space (m), or null for the legacy
 * bowl whose dugout sits `BENCH_Z_OFF` outside the touchline.
 *
 * ⚠ THIS IS THE "row of men standing on the grass" the owner photographed
 * twice (2026-08-15, 2026-08-16). The substitutes were placed 2.6 m outside
 * the touchline — correct for `stadium.ts`'s bowl — while the shipped
 * FLAGSHIP bowl models its dugouts at z = -44: from
 * `assets/stadium-v5/data/markers.json` (MARKER_RainOcclusion_Dugout* at
 * model x = 44) mapped through `V5_BOWL_YAW`, a mapping double-checked by the
 * fact that it also lands the modelled goals on game x = +/-50. So eleven
 * substitutes stood in a line SEVEN METRES SHORT of the shelter they were
 * supposed to be sitting under.
 *
 * They stand rather than sit because the character pack has no seated clip;
 * inside the dugout the structure hides them from the shins down, which reads
 * as a bench. A real seated pose is authoring work, logged as owed.
 */
export const FLAGSHIP_DUGOUT_Z: number | null = -44;
/** Gap between one sub and the next, metres. */
export const BENCH_SPACING_M = 1.15;

export function benchPlacement(
  side: 'home' | 'away',
  index: number,
  count: number,
  pitch: PitchDims,
): Placement {
  const z = FLAGSHIP_DUGOUT_Z ?? -(pitch.width * 0.5 + BENCH_Z_OFF);
  const cx = side === 'home' ? -DUGOUT_CX : DUGOUT_CX;
  // Centre the row on the dugout however many are named, so a seven-man bench
  // and a three-man one both sit under their own shelter rather than running
  // off one end of it.
  const spread = (count - 1) * BENCH_SPACING_M;
  const x = cx - spread * 0.5 + index * BENCH_SPACING_M;
  // Facing the pitch, like the coach.
  return { x, z, yaw: Math.PI / 2 };
}
